/**
 * Something failed — say what, and give a way out. Never a bare spinner that never resolves.
 *
 * Author: Danish Khan
 */
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api.ts';
import { Button } from './Button.tsx';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** What the user was trying to do, e.g. "load the inbox". */
  context?: string;
}

export function ErrorState({ error, onRetry, context }: ErrorStateProps): ReactNode {
  const status = error instanceof ApiError ? error.status : null;
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Something went wrong.';
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span className="rounded-full bg-danger-soft px-3 py-1 text-xs font-semibold text-danger-soft-fg">
        {status ? `Error ${status}` : 'Error'}
      </span>
      <p className="max-w-md text-sm font-medium text-fg">
        {context ? `Could not ${context}.` : 'Request failed.'}
      </p>
      <p className="max-w-md text-sm text-fg-muted">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="secondary">
          Try again
        </Button>
      )}
    </div>
  );
}
